import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const ResetPassword = () => {
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword,    setShowPassword]    = useState(false);
  const [status,          setStatus]          = useState('idle'); // idle | loading | success | error
  const [errorMsg,        setErrorMsg]        = useState('');
  const [sessionReady,    setSessionReady]    = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setSessionReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async () => {
    setErrorMsg('');

    if (password.length < 8) {
      setErrorMsg('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setStatus('loading');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setErrorMsg(error.message);
      setStatus('error');
    } else {
      await supabase.auth.signOut(); // force fresh login after reset
      setStatus('success');
    }
  };

  const passwordStrength =
    password.length === 0  ? { width: '0%',   color: 'transparent', label: '' } :
    password.length < 8    ? { width: '33%',  color: '#d9534f',     label: 'Too short' } :
    password.length < 12   ? { width: '66%',  color: '#BC6C25',     label: 'Good' } :
                             { width: '100%', color: '#606C38',     label: 'Strong ✓' };

  // ── Waiting for recovery link ─────────────────────────────────────────────
  if (!sessionReady && status === 'idle') {
    return (
      <div style={pageWrapper}>
        <div style={card}>
          <div style={logoMark}>S</div>
          <p style={kickerStyle}>SayCiao.school</p>
          <h1 style={headingStyle}>Waiting for link...</h1>
          <p style={bodyText}>
            If you arrived here from your email, the page should load in a moment.
            If nothing happens, the link may have expired — request a new one from your dashboard.
          </p>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div style={pageWrapper}>
        <div style={card}>
          <div style={{ ...logoMark, backgroundColor: '#283618' }}>✓</div>
          <p style={kickerStyle}>All done</p>
          <h1 style={headingStyle}>Password updated!</h1>
          <p style={bodyText}>
            Your new password is saved. You can now log in to your student portal.
          </p>
          <a href="/portal" style={buttonStyle}>Back to Portal</a>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────
  return (
    <div style={pageWrapper}>
      <div style={card}>
        <div style={logoMark}>S</div>
        <p style={kickerStyle}>SayCiao.School</p>
        <h1 style={headingStyle}>Choose a new password</h1>
        <p style={bodyText}>Pick something memorable — at least 8 characters.</p>

        <div style={formGroup}>
          <label style={labelStyle}>New Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              style={toggleBtn}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <div style={formGroup}>
          <label style={labelStyle}>Confirm Password</label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••••••"
            style={{
              ...inputStyle,
              borderColor: !confirmPassword           ? '#eee'
                : confirmPassword !== password        ? '#d9534f'
                : '#606C38',
            }}
          />
          {confirmPassword && confirmPassword === password && (
            <p style={{ ...hintText, color: '#606C38' }}>✓ Passwords match</p>
          )}
        </div>

        {errorMsg && <p style={errorStyle}>{errorMsg}</p>}

        <div style={strengthBarContainer}>
          <div style={{ ...strengthBarFill, width: passwordStrength.width, backgroundColor: passwordStrength.color }} />
        </div>
        <p style={hintText}>{passwordStrength.label}</p>

        <button
          onClick={handleSubmit}
          disabled={status === 'loading'}
          style={{ ...buttonStyle, opacity: status === 'loading' ? 0.6 : 1, cursor: status === 'loading' ? 'not-allowed' : 'pointer', marginTop: '10px' }}
        >
          {status === 'loading' ? 'Saving...' : 'Save New Password'}
        </button>
      </div>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const TERRACOTTA = '#BC6C25';
const DEEP_MOSS  = '#283618';
const PAPER      = '#FCF9F2';

const pageWrapper = {
  minHeight: '100vh',
  backgroundColor: PAPER,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  fontFamily: "'Montserrat', sans-serif",
};

const card = {
  backgroundColor: '#fff',
  padding: '50px 40px',
  borderRadius: '20px',
  boxShadow: '0 15px 40px rgba(40, 54, 24, 0.07)',
  border: '1px solid rgba(188, 108, 37, 0.1)',
  width: '100%',
  maxWidth: '420px',
  textAlign: 'center',
};

const logoMark = {
  width: '52px',
  height: '52px',
  borderRadius: '50%',
  backgroundColor: TERRACOTTA,
  color: '#fff',
  fontSize: '1.3rem',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 20px',
  fontFamily: "'Playfair Display', serif",
};

const kickerStyle = {
  color: TERRACOTTA,
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '3px',
  marginBottom: '8px',
  fontWeight: 600,
};

const headingStyle = {
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 300,
  fontSize: '1.8rem',
  color: DEEP_MOSS,
  margin: '0 0 12px',
  letterSpacing: '1px',
};

const bodyText = {
  fontSize: '0.85rem',
  color: '#999',
  lineHeight: '1.6',
  margin: '0 0 30px',
};

const formGroup  = { textAlign: 'left', marginBottom: '20px' };

const labelStyle = {
  display: 'block',
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: '#999',
  marginBottom: '8px',
  fontWeight: 600,
};

const inputStyle = {
  width: '100%',
  padding: '13px 15px',
  borderRadius: '10px',
  border: '1px solid #eee',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: "'Montserrat', sans-serif",
  transition: 'border-color 0.2s ease',
};

const toggleBtn = {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  color: TERRACOTTA,
  fontSize: '0.6rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  cursor: 'pointer',
  padding: '5px',
};

const buttonStyle = {
  display: 'block',
  width: '100%',
  backgroundColor: TERRACOTTA,
  color: '#fff',
  border: 'none',
  padding: '15px',
  borderRadius: '10px',
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  textDecoration: 'none',
  textAlign: 'center',
  fontFamily: "'Montserrat', sans-serif",
  marginTop: '20px',
  transition: 'opacity 0.2s ease',
};

const strengthBarContainer = {
  height: '4px',
  backgroundColor: '#eee',
  borderRadius: '10px',
  overflow: 'hidden',
  marginTop: '5px',
};

const strengthBarFill = {
  height: '100%',
  borderRadius: '10px',
  transition: 'width 0.3s ease, background-color 0.3s ease',
};

const hintText = {
  fontSize: '0.65rem',
  color: TERRACOTTA,
  marginTop: '5px',
  textAlign: 'left',
  minHeight: '16px',
};

const errorStyle = {
  color: '#d9534f',
  fontSize: '0.8rem',
  marginBottom: '15px',
  textAlign: 'left',
  backgroundColor: '#fff5f5',
  padding: '10px 15px',
  borderRadius: '8px',
  border: '1px solid rgba(217, 83, 79, 0.2)',
};

export default ResetPassword;